import { create } from 'zustand';

import type { UserModel, UserResponse } from '@/domain/models/user.js';
import { UserModel as UserModelClass } from '@/domain/models/user.js';

interface AuthState {
    user: UserModel | null;
    accessToken: string | null;
    refreshToken: string | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    sessionExpired: boolean;
    setAuth: (user: UserModel, accessToken: string, refreshToken: string) => void;
    setUser: (user: UserModel) => void;
    clearAuth: () => void;
    expireSession: () => void;
    acknowledgeSessionExpired: () => void;
    setLoading: (loading: boolean) => void;
}

function loadSavedState() {
    try {
        const token = localStorage.getItem('accessToken');
        const saved = localStorage.getItem('user');
        if (token && saved) {
            const raw = JSON.parse(saved) as UserResponse;
            return {
                user: UserModelClass.withFromResponse(raw),
                accessToken: token,
                refreshToken: localStorage.getItem('refreshToken') ?? '',
                isAuthenticated: true,
                isLoading: false
            };
        }
    } catch {
        // ignore
    }
    return { user: null, accessToken: null, refreshToken: null, isAuthenticated: false, isLoading: false };
}

export const useAuthStore = create<AuthState>((set, get) => ({
    ...loadSavedState(),
    sessionExpired: false,
    setAuth: (user, accessToken, refreshToken) => {
        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('refreshToken', refreshToken);
        localStorage.setItem('user', JSON.stringify(user.toObject()));
        set({ user, accessToken, refreshToken, isAuthenticated: true, isLoading: false, sessionExpired: false });
    },
    setUser: (user) => {
        localStorage.setItem('user', JSON.stringify(user.toObject()));
        set((s) => ({ ...s, user }));
    },
    clearAuth: () => {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        set({ user: null, accessToken: null, refreshToken: null, isAuthenticated: false, isLoading: false, sessionExpired: false });
    },
    expireSession: () => {
        const wasAuthenticated = get().isAuthenticated;
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        set({ user: null, accessToken: null, refreshToken: null, isAuthenticated: false, isLoading: false, sessionExpired: wasAuthenticated });
    },
    acknowledgeSessionExpired: () => set((s) => ({ ...s, sessionExpired: false })),
    setLoading: (loading) => set((s) => ({ ...s, isLoading: loading }))
}));
